/**
 * @file OrderChainPanel.tsx
 * @description Блок «Путь заказа»: шкала стадий + список документов
 * (phase19.24-order-card-chain-design.md §2, §5, §8).
 *
 * Заменяет три прежних места: степпер, колонку «Связи» и вкладку «Путь».
 * Стадия отвечает «где заказ», документ — «чем подтверждено», поэтому шкала и
 * список живут в одном блоке и подсвечивают одно и то же событие.
 *
 * Блок не держит страницу: шапка и плитки уже отрисованы по `GET /orders/:id`,
 * а здесь показывается скелет, полоса деградации источника или ошибка с
 * «Повторить». Закрытый заказ сворачивается до шкалы и строки «пройден».
 */

import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import { OrderChainGroup } from "./OrderChainGroup";
import { OrderChainNode } from "./OrderChainNode";
import { OrderChainStepper } from "./OrderChainStepper";
import type { OrderChainNodeDto, OrderChainState } from "../../../lib/order-chain";
import { nextManagerStage } from "../../../lib/order-chain";
import { resolveChainLink, type ChainLinkContext } from "../../../lib/order-chain-links";
import { groupChainNodes } from "../../../lib/order-chain-node-view";
import { Button } from "../../ui/button";
import { EditorialCard } from "../../ui/EditorialCard";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { SectionLabel } from "../../ui/SectionLabel";
import { Skeleton } from "../../ui/skeleton";

interface Props {
  chain: OrderChainState;
  /** Слаг и заказ — из них строятся адреса узлов. */
  linkContext: ChainLinkContext;
  /** Заказ завершён или отменён: путь сворачиваем до шкалы. */
  closed: boolean;
}

export function OrderChainPanel({ chain, linkContext, closed }: Props) {
  const [expanded, setExpanded] = useState(false);
  const data = chain.data;
  const open = !closed || expanded;
  // «этап 4 из 9» показываем, только когда матрица маршрута известна (§2):
  // пока её нет, сервер шлёт null, и строку прячем, а не пишем «этап 0 из 0».
  const stageCounter =
    data?.route.currentIndex != null && data.route.totalCount
      ? `этап ${data.route.currentIndex} из ${data.route.totalCount}`
      : null;

  const hrefOf = (node: OrderChainNodeDto) =>
    node.hidden === "no_permission" ? null : resolveChainLink(node.link, linkContext);

  return (
    <EditorialCard id="chain">
      <EditorialCard.Body className="flex flex-col gap-4">
        <SectionLabel
          as="h2"
          trailing={
            <div className="flex items-center gap-3">
              {stageCounter ? (
                <span className="text-xs tabular-nums text-on-surface-variant">
                  {stageCounter}
                </span>
              ) : null}
              {closed ? (
                // Стандартная кнопка-контур со значком: «призрачная» читалась
                // как обычный текст, а не действие (приёмка qa4 п. 11).
                <Button
                  variant="outline"
                  onClick={() => setExpanded((v) => !v)}
                  aria-expanded={expanded}
                >
                  {expanded ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
                  {expanded ? "Свернуть" : "Показать документы"}
                </Button>
              ) : null}
            </div>
          }
        >
          Путь заказа{data?.route.label ? ` · ${data.route.label}` : ""}
        </SectionLabel>

        {chain.error ? (
          <NoticeStrip
            tone="error"
            action={
              <Button variant="outline" onClick={chain.reload}>
                Повторить
              </Button>
            }
          >
            Не удалось собрать путь заказа. Остальная карточка работает.
          </NoticeStrip>
        ) : null}

        {data?.degraded.map((item) => (
          <NoticeStrip
            key={item.source}
            tone="warning"
            action={
              <Button variant="outline" onClick={chain.reload}>
                Повторить
              </Button>
            }
          >
            {item.message}
          </NoticeStrip>
        ))}

        {chain.loading && !data ? <ChainSkeleton /> : null}

        {data ? (
          <>
            <OrderChainStepper
              route={data.route}
              nextManager={nextManagerStage(data.route)}
            />

            {closed && !expanded ? (
              <p className="text-sm text-on-surface-variant">
                Путь пройден · {data.nodes.length} документов
              </p>
            ) : null}

            {open ? <ChainList nodes={data.nodes} hrefOf={hrefOf} onRetry={chain.reload} /> : null}
          </>
        ) : null}
      </EditorialCard.Body>
    </EditorialCard>
  );
}

/** Список узлов с группировкой однотипных. */
function ChainList({
  nodes,
  hrefOf,
  onRetry,
}: {
  nodes: OrderChainNodeDto[];
  hrefOf: (node: OrderChainNodeDto) => string | null;
  onRetry: () => void;
}) {
  if (nodes.length === 0) {
    return (
      <p className="text-sm text-on-surface-variant">
        Документов по заказу пока нет — они появятся по ходу оплаты и сборки.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1">
      {groupChainNodes(nodes).map((entry) =>
        entry.kind === "group" ? (
          <OrderChainGroup
            key={`group-${entry.groupKey}`}
            nodes={entry.nodes}
            hrefOf={hrefOf}
            onRetry={onRetry}
          />
        ) : (
          <OrderChainNode
            key={`${entry.node.key}-${entry.node.number ?? entry.node.createdAt ?? ""}`}
            node={entry.node}
            href={hrefOf(entry.node)}
            onRetry={onRetry}
          />
        ),
      )}
    </ul>
  );
}

/** Скелет блока: шкала и четыре строки 56px — как в §8. */
function ChainSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-6 w-full" />
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}
